import type { EntryPoint, EntryPointComponent } from 'react-relay';
import type { EntryPointRouteParams } from '@jira/platform__entry-points-plugin-types/src/types.tsx';
import { auditLogPanelEntryPoint } from '@jira/servicedesk__proactive-support-audit-log/src/auditLogPanel.entrypoint.tsx';
import { dashboardPanelEntryPoint } from '@jira/servicedesk__proactive-support/src/dashboardPanel.entrypoint.tsx';
import { openIssuesPanelEntryPoint } from '@jira/servicedesk__proactive-support/src/openIssuesPanel.entrypoint.tsx';
import { devicesPanelEntryPoint } from '@jira/servicedesk__proactive-support/src/devicesPanel.entrypoint.tsx';
import { createEntryPoint } from '@atlassian/react-entrypoint';
import { JSResourceForUserVisible } from '@atlassian/react-async';
import type {
	ProactiveSupportPanelEntryPoints,
	ServicedeskProactiveSupportAppExtraProps,
} from './servicedeskProactiveSupportAppExtraProps.entrypoint.types.tsx';

type ServiceProactiveSupportEntryPointComponent = EntryPointComponent<
	Record<string, never>,
	ProactiveSupportPanelEntryPoints,
	{},
	ServicedeskProactiveSupportAppExtraProps
>;

export const servicedeskProactiveSupportEntryPoint: EntryPoint<
	ServiceProactiveSupportEntryPointComponent,
	EntryPointRouteParams
> = createEntryPoint({
	root: JSResourceForUserVisible<ServiceProactiveSupportEntryPointComponent>(
		/* webpackChunkName: "async-servicedesk-proactive-support", jiraSpaEntry: "async-servicedesk-proactive-support" */ './ServicedeskProactiveSupportApp.tsx',
		{
			entryFsSsrLiftImportToModule: true,
		},
	),
	// Route names mirror the definitions in `@jira/router-routes__servicedesk-proactive-support-routes`.
	// Only the active route's panel is preloaded so the other panel chunks stay lazy.
	getPreloadProps: ({ context: { match, route } }: EntryPointRouteParams) => {
		const isOpenIssues = route.name === 'servicedesk-proactive-support-open-issues';
		const isDevices = route.name === 'servicedesk-proactive-support-devices';
		const isAuditLog = route.name === 'servicedesk-proactive-support-audit-log';

		return {
			extraProps: {
				projectKey: String(match.params.projectKey ?? ''),
			},
			entryPoints: {
				dashboard:
					!isOpenIssues && !isDevices && !isAuditLog
						? { entryPoint: dashboardPanelEntryPoint, entryPointParams: {} }
						: undefined,
				'open-issues': isOpenIssues
					? { entryPoint: openIssuesPanelEntryPoint, entryPointParams: {} }
					: undefined,
				devices: isDevices
					? { entryPoint: devicesPanelEntryPoint, entryPointParams: {} }
					: undefined,
				'audit-log': isAuditLog
					? { entryPoint: auditLogPanelEntryPoint, entryPointParams: {} }
					: undefined,
			},
		};
	},
});
